import { type ReactNode, useMemo } from 'react';
import { Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useGroupTierDrafts } from '../hooks/use-group-tier-drafts';
import {
  validateGroupTiers,
  tierFieldErrors,
} from '../utils/validate-group-tiers';
import { GroupTierEditor } from './group-tier-editor';

type Drafts = ReturnType<typeof useGroupTierDrafts>;

function Title(): ReactNode {
  return (
    <div className="flex items-center gap-sm">
      <Users size={14} className="text-text-tertiary" />
      <span className="text-xs font-medium text-text-secondary">
        Bonus de equipo por media diaria
      </span>
    </div>
  );
}

function Errors({ errs }: { errs: string[] }): ReactNode {
  if (!errs.length) return null;
  return (
    <ul className="mt-xs space-y-0.5 text-[11px] text-error">
      {errs.map((e, i) => (
        <li key={i}>• {e}</li>
      ))}
    </ul>
  );
}

function Actions({ d, disabled }: { d: Drafts; disabled: boolean }): ReactNode {
  return (
    <div className="flex justify-end">
      <Button size="sm" onClick={() => void d.save()} disabled={disabled}>
        Guardar tramos
      </Button>
    </div>
  );
}

function Body({ d }: { d: Drafts }): ReactNode {
  const errors = useMemo(() => validateGroupTiers(d.drafts), [d.drafts]);
  const fieldErrs = useMemo(() => tierFieldErrors(d.drafts), [d.drafts]);
  const saveDisabled = !d.dirty || d.saving || errors.length > 0;
  return (
    <>
      <GroupTierEditor
        tiers={d.drafts}
        errors={fieldErrs}
        onChange={d.setTiers}
      />
      <Errors errs={errors} />
      <Actions d={d} disabled={saveDisabled} />
    </>
  );
}

export function GroupTierList(): ReactNode {
  const d = useGroupTierDrafts();
  return (
    <div className="col-span-full space-y-sm">
      <Title />
      {d.loading ? (
        <p className="text-xs text-text-tertiary">Cargando tramos…</p>
      ) : (
        <Body d={d} />
      )}
      <p className="text-[10px] text-text-tertiary">
        Medias por debajo del primer tramo no reciben bonus. Arrastra tramos
        intermedios para reordenar. El último tramo es abierto (∞).
      </p>
    </div>
  );
}
